<template>
  <div class="quota-pre-page">
    <a-spin :spinning="loading">
      <PlateTitle title="投保申请解密" />
      <a-alert
        v-if="errorMessage"
        type="error"
        show-icon
        :message="errorMessage"
        class="alert"
      />
      <a-form
        v-else
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 14 }"
        class="pre-form"
      >
        <a-form-item label="业务流水号">
          <span>{{ predata.applyNo || orderNo }}</span>
        </a-form-item>
        <a-form-item label="投保申请数据">
          <span class="break-text">{{ predata.applyData }}</span>
        </a-form-item>
        <a-form-item label="数字信封">
          <span class="break-text">{{ predata.digitalEnvelope }}</span>
        </a-form-item>
        <a-form-item :wrapper-col="{ offset: 6, span: 14 }">
          <a-button
            v-if="decryptActions.ca"
            type="primary"
            size="large"
            :loading="caLoading"
            :disabled="!canUseCa"
            @click="decryptByCa"
          >
            CA锁解密
          </a-button>
          <a-button
            v-if="decryptActions.bzt"
            type="primary"
            size="large"
            class="action-button"
            :loading="bztLoading"
            @click="decryptByBzt"
          >
            标证通解密
          </a-button>
        </a-form-item>
      </a-form>
    </a-spin>

    <a-modal
      v-model="qrcodeVisible"
      title="请使用标证通扫码解密"
      centered
      :footer="null"
      :mask-closable="false"
      @cancel="stopBztPolling"
    >
      <img
        v-show="qrcodeImage"
        :src="qrcodeImage"
        class="qrcode-image"
        alt="标证通二维码"
      >
      <div
        v-if="!qrcodeImage"
        class="qrcode-loading"
      >
        二维码生成中...
      </div>
    </a-modal>

    <iframe
      v-if="decryptActions.ca"
      ref="caBridge"
      class="ca-bridge"
      src="/static/quota-ca-312/bridge.html"
      title="CA解密桥接"
      sandbox="allow-scripts allow-same-origin"
    />
  </div>
</template>

<script>
/* global QRCode */
import PlateTitle from '@/components/PlateTitle';
import {
  getBztQrcode,
  getBztQueryResult,
  getPredata,
  getUiProfile,
  postDigitalEnvelope,
} from '@/api/quota';

const {
  buildBztQrcodeParams,
  buildBztQueryParams,
  buildDigitalEnvelopePayload,
  buildEnvelopeCandidates,
  extractBztQrcode,
  getDecryptActions,
  getDecryptResponseAction,
  isPollingGenerationActive,
  isTrustedBridgeMessage,
  resolveSafeQuotaRedirect,
} = require('./policy');

const EMPTY_ACTIONS = { ca: false, bzt: false };

export default {
  name: 'InsurePreQuota',
  components: { PlateTitle },
  data() {
    return {
      loading: false,
      caLoading: false,
      bztLoading: false,
      errorMessage: '',
      uiProfile: {},
      decryptActions: { ...EMPTY_ACTIONS },
      predata: {},
      qrcodeVisible: false,
      qrcodeImage: '',
      qrcodeid: '',
      bztTimer: null,
      bztGeneration: 0,
      caTimer: null,
      caNonce: '',
    };
  },
  computed: {
    channelCode() {
      return this.$route.query.channelCode || '';
    },
    orderNo() {
      return this.$route.query.orderNo || '';
    },
    canUseCa() {
      return this.uiProfile.caProfile === 'EPOINT_IWEBNET' &&
        Number(this.uiProfile.caType) === 312;
    },
  },
  created() {
    window.addEventListener('message', this.handleCaMessage);
    this.initialize();
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleCaMessage);
    this.stopBztPolling();
    this.clearCaRequest();
  },
  methods: {
    unwrap(response) {
      const body = response && response.data;
      if (body && body.data && typeof body.data === 'object') return body.data;
      return body || {};
    },
    notifyError(error, fallback) {
      const responseMessage = error && error.response && error.response.data &&
        error.response.data.message;
      const message = responseMessage || (error && error.message) || fallback;
      this.$notification.error({ message });
    },
    async initialize() {
      if (!this.channelCode || !this.orderNo) {
        this.errorMessage = '缺少渠道编码或业务流水号';
        return;
      }
      this.loading = true;
      try {
        this.uiProfile = this.unwrap(await getUiProfile(this.channelCode, this.orderNo));
        this.decryptActions = getDecryptActions(this.uiProfile.decryptMode);
        if (this.uiProfile.decryptMode === 'NONE') {
          this.goToInsure();
          return;
        }
        if (this.decryptActions.ca && !this.canUseCa) {
          throw new Error('当前渠道CA配置不受支持');
        }
        const predata = this.unwrap(await getPredata(this.channelCode, this.orderNo));
        const predataAction = getDecryptResponseAction(predata);
        if (predataAction.toInsure) {
          this.goToInsure(predataAction.redirectUrl);
          return;
        }
        this.predata = predata;
        if (this.predata.applyNo && this.predata.applyNo !== this.orderNo) {
          throw new Error('预解密数据与当前业务流水号不一致');
        }
      } catch (error) {
        this.errorMessage = error.message || '预解密数据加载失败';
        this.notifyError(error, '预解密数据加载失败');
      } finally {
        this.loading = false;
      }
    },
    createNonce() {
      if (!window.crypto || !window.crypto.getRandomValues) {
        throw new Error('当前浏览器不支持安全的CA解密会话');
      }
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return Array.prototype.map.call(bytes, value => value.toString(16).padStart(2, '0')).join('');
    },
    decryptByCa() {
      if (!this.predata.digitalEnvelope) {
        this.$notification.warning({ message: '数字信封为空' });
        return;
      }
      const bridgeWindow = this.$refs.caBridge && this.$refs.caBridge.contentWindow;
      if (!bridgeWindow) {
        this.$notification.error({ message: 'CA解密组件尚未加载' });
        return;
      }
      try {
        this.clearCaRequest();
        this.caNonce = this.createNonce();
        this.caLoading = true;
        bridgeWindow.postMessage({
          type: 'QUOTA_CA_DECRYPT',
          nonce: this.caNonce,
          caType: 312,
          envelopes: buildEnvelopeCandidates(this.predata.digitalEnvelope),
        }, window.location.origin);
        this.caTimer = window.setTimeout(() => {
          this.clearCaRequest();
          this.$notification.error({ message: 'CA解密超时，请检查CA锁及驱动' });
        }, 20000);
      } catch (error) {
        this.clearCaRequest();
        this.notifyError(error, 'CA解密启动失败');
      }
    },
    async handleCaMessage(event) {
      const bridgeWindow = this.$refs.caBridge && this.$refs.caBridge.contentWindow;
      if (!this.caNonce || !isTrustedBridgeMessage(event, {
        origin: window.location.origin,
        source: bridgeWindow,
        nonce: this.caNonce,
      })) return;

      const message = event.data;
      this.clearCaRequest();
      if (message.type === 'QUOTA_CA_ERROR') {
        this.$notification.error({ message: message.message || 'CA解密失败' });
        return;
      }
      try {
        this.caLoading = true;
        const payload = buildDigitalEnvelopePayload(this.orderNo, message.decrypted);
        const result = this.unwrap(await postDigitalEnvelope(this.channelCode, payload));
        this.goToInsure(result.redirectUrl);
      } catch (error) {
        this.notifyError(error, 'CA解密结果提交失败');
      } finally {
        this.caLoading = false;
      }
    },
    clearCaRequest() {
      if (this.caTimer) window.clearTimeout(this.caTimer);
      this.caTimer = null;
      this.caNonce = '';
      this.caLoading = false;
    },
    async decryptByBzt() {
      this.stopBztPolling();
      const generation = this.bztGeneration;
      this.bztLoading = true;
      this.qrcodeVisible = true;
      try {
        const response = await getBztQrcode(
          this.channelCode,
          buildBztQrcodeParams(this.orderNo)
        );
        if (generation !== this.bztGeneration || !this.qrcodeVisible) return;
        const qrcodeData = this.unwrap(response);
        const qrcodeAction = getDecryptResponseAction(qrcodeData);
        if (qrcodeAction.toInsure) {
          this.stopBztPolling();
          this.goToInsure(qrcodeAction.redirectUrl);
          return;
        }
        const qrInfo = extractBztQrcode(qrcodeData);
        this.qrcodeid = qrInfo.qrcodeid;
        const qrcodeImage = await new Promise((resolve, reject) => {
          QRCode.toDataURL(qrInfo.content, (error, url) => {
            if (error) reject(error);
            else resolve(url);
          });
        });
        if (!isPollingGenerationActive(
          generation,
          this.bztGeneration,
          this.qrcodeVisible,
          this.qrcodeid
        )) return;
        this.qrcodeImage = qrcodeImage;
        this.pollBztResult(generation);
      } catch (error) {
        if (generation !== this.bztGeneration) return;
        this.stopBztPolling();
        this.notifyError(error, '标证通二维码生成失败');
      } finally {
        if (generation === this.bztGeneration) this.bztLoading = false;
      }
    },
    async pollBztResult(generation) {
      if (!isPollingGenerationActive(
        generation,
        this.bztGeneration,
        this.qrcodeVisible,
        this.qrcodeid
      )) return;
      if (this.bztTimer) window.clearTimeout(this.bztTimer);
      try {
        const result = this.unwrap(await getBztQueryResult(
          this.channelCode,
          buildBztQueryParams(this.orderNo, this.qrcodeid)
        ));
        if (!isPollingGenerationActive(
          generation,
          this.bztGeneration,
          this.qrcodeVisible,
          this.qrcodeid
        )) return;
        const resultAction = getDecryptResponseAction(result);
        if (resultAction.toInsure) {
          this.stopBztPolling();
          this.goToInsure(resultAction.redirectUrl);
          return;
        }
        if (String(result.qrstatus) === '03') {
          this.stopBztPolling();
          this.$notification.warning({ message: result.message || '标证通解密未完成' });
          return;
        }
        this.bztTimer = window.setTimeout(() => this.pollBztResult(generation), 10000);
      } catch (error) {
        if (generation !== this.bztGeneration) return;
        this.stopBztPolling();
        this.notifyError(error, '标证通解密状态查询失败');
      }
    },
    stopBztPolling() {
      this.bztGeneration += 1;
      if (this.bztTimer) window.clearTimeout(this.bztTimer);
      this.bztTimer = null;
      this.qrcodeVisible = false;
      this.qrcodeid = '';
      this.qrcodeImage = '';
      this.bztLoading = false;
    },
    goToInsure(redirectUrl) {
      window.location.assign(resolveSafeQuotaRedirect(redirectUrl, {
        origin: window.location.origin,
        channelCode: this.channelCode,
        orderNo: this.orderNo,
      }));
    },
  },
};
</script>

<style lang="less" scoped>
.quota-pre-page {
  min-height: 620px;
  margin-top: 30px;
  padding: 30px 50px 80px;
  background: #fff;
}
.alert { margin: 30px 0; }
.pre-form { margin-top: 30px; }
.break-text { word-break: break-all; }
.action-button { margin-left: 24px; }
.qrcode-image {
  display: block;
  width: 320px;
  height: 320px;
  margin: 20px auto;
}
.qrcode-loading {
  padding: 140px 0;
  text-align: center;
  color: #999;
}
.ca-bridge {
  position: fixed;
  width: 1px;
  height: 1px;
  left: -10000px;
  border: 0;
}
</style>
